import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { EmployeesSelectorComponent } from 'src/app/employees/employees-selector/employees-selector.component';
import { MessageEditComponent } from 'src/app/messages/message-edit/message-edit.component';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { RBACService } from 'src/app/shared/rbac.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { CustomDataSource } from 'src/app/utils/custom-data-source';
import { UserPublicProfileComponent } from '../../shared/user-public-profile/user-public-profile.component';

@Component({
  selector: 'app-sites-managers',
  templateUrl: './sites-managers.component.html',
  styleUrls: ['./sites-managers.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SitesManagersComponent implements OnInit, AfterViewInit {
  // the site id must be present
  parent_id: number;
  child_id: number;
  site_id: number;

  // columns to show and the data source
  displayedColumns: string[] = [
    'select',
    'contact_person',
    'email',
    'mobile',
    'trade',
    'actions'
  ];
  dataSource: SitesManagersDataSource;

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  // RBAC permission flags. Resolved once in ngOnInit() so the template does not
  // re-evaluate permission lookups on every change detection cycle.
  canEdit: boolean = false;
  canCreateMessages: boolean = false;

  constructor(
    // public service: ContractorsService,
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public route: ActivatedRoute,
    public rbac: RBACService,
    private router: Router
  ) {}

  ngOnInit() {
    // Resolve the RBAC permission flags first so they are always set, even when
    // the access guard below short circuits the rest of this method.
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_MANAGERS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canCreateMessages = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_MESSAGES,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );

    // Check permissions - redirect if unauthorized
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_MANAGERS,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view site managers.'
      );
      this.utils.redirectByLastSegment('details');
      return;
    }

    // Hide checkbox column if user doesn't have permissions for any bulk actions
    // Checkbox is used for: bulk unlink (requires edit) OR send message (requires messages:create)
    // Show checkbox if user has EITHER permission
    if (!this.canEdit && !this.canCreateMessages) {
      this.displayedColumns = this.displayedColumns.filter(
        (col) => col !== 'select'
      );
    }

    // Get the site id from the route params.
    this.parent_id = Number(this.route.parent.snapshot.params['parent_id']);
    this.child_id = Number(this.route.parent.snapshot.params['child_id']);

    // Check if we are updating a site or creating a new one.
    if (
      this.child_id ||
      (typeof this.route.parent.snapshot.params['child_id'] == 'undefined' &&
        this.parent_id)
    ) {
      // Store the site id.
      this.site_id = this.child_id ? this.child_id : this.parent_id;
      // load a multi select data source object
      this.dataSource = new SitesManagersDataSource(this.app, this.api, true, {
        site_id: this.site_id
      });
      // get the data only when the site id is present
      this.dataSource.getData();
    }
  }

  ngAfterViewInit() {
    // reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_by = this.sort.active;
      this.dataSource.sort_order = this.sort.direction;

      this.dataSource.getData();
    });

    // subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   *
   * @param id the contractor id
   *
   * unlink a single contractor
   */
  onRemove(id: number) {
    // Check permissions - return if unauthorized
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_MANAGERS,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to unlink managers.'
      );
      return;
    }

    this.utils.showModal(
      'Unlink Manager',
      'Are you sure you want to unlink this Manager?',
      () => {
        this.onUpdateSiteManagers(
          this.dataSource.employee_ids.filter(
            (manager_id) => ![id].includes(manager_id)
          ),
          (response) => {
            this.dataSource.selection.deselect(id);
          }
        );
      }
    );
  }

  /**
   * unlink selected contractors from the specified site
   */
  onDeleteSelected() {
    // Check permissions - return if unauthorized
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_MANAGERS,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to unlink managers.'
      );
      return;
    }

    this.utils.showModal(
      'Unlink Selected Managers',
      'Are you sure you want to unlink the selected Managers?',
      () => {
        this.onUpdateSiteManagers(
          this.dataSource.employee_ids.filter(
            (contractor_id) =>
              !this.dataSource.selection.selected.includes(contractor_id)
          ),
          (response) => {
            this.dataSource.selection.clear();
          }
        );
      }
    );
  }

  /**
   * open a dialog allowing the user to select contractors to be linked to sites.
   */
  onLinkContractorBusiness() {
    // Check permissions - return if unauthorized
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_MANAGERS,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to link employees as managers.'
      );
      return;
    }

    // show the site selector
    this.utils.showComponentDialog(
      EmployeesSelectorComponent,
      {
        multiple: true,
        selected: this.dataSource.employee_ids
      },
      {
        width: '1200px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.dataSource.employee_ids = results;

          if (this.site_id) {
            this.onUpdateSiteManagers(this.dataSource.employee_ids);
          }
        }
      }
    );
  }

  onUpdateSiteManagers(employee_ids: any, callback?: any) {
    if (this.site_id) {
      // link all contractor businesses
      this.api
        .makeRequest('put', 'v1/sites/' + this.site_id + '/managers', {
          selected_employee_ids: employee_ids
        })
        .then((response: any) => {
          // reload the data and reset the offset
          this.dataSource.getData(true);

          if (typeof callback === 'function') {
            callback(response);
          }
        })
        .catch((err: any) => {
          this.utils.showModal('Error', err.error.message);
        });
    }
  }

  composeNewMessage() {
    // Check permissions - return if unauthorized
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_MESSAGES,
            RBACService.RBAC_A.A_CREATE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to send messages.'
      );
      return;
    }

    if (this.dataSource.selection.selected.length == 0) {
      this.utils.showToast('You need to select some Managers first.');
      return; // early termination
    }

    this.utils
      .showComponentDialog(
        MessageEditComponent,
        {
          preSelectUserIds: this.dataSource.selection.selected
        },
        {
          width: '1200px'
        }
      )
      .then((response) => {
        if (response) {
          this.utils.showToast(
            'You can view the message stats in the messages section.'
          );
        }
      });
  }

  onUserPublicView(hash: string) {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData();
      }
    );
  }

  // Expose RBACService to template
  protected readonly RBACService = RBACService;
}

export class SitesManagersDataSource extends CustomDataSource {
  sort_by = 'contact_person';
  sort_order = 'asc';

  employee_ids: number[] = [];

  getData(resetOffset: boolean = false) {
    this.getDataFromLaravelAPI(
      'sites/' + this['site_id'] + '/managers',
      resetOffset,
      (response) => {
        this.employee_ids = response.meta.user_ids;
      }
    );
  }
}
